import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import MCQViewer from "./MCQViewer";
import TrueFalseViewer from "./TrueFalseViewer";
import MatchViewer from "./MatchViewer";
import FillUpsViewer from "./FillUpsViewer";
import AnalysisComponent from "./AnalysisComponent";
import GlobalLoader from "./GlobalLoader";
import GenerateQuestionBankPopup from "./GenerateQuestionBankPopup";
import {
  getQuestionBankById,
  updateQuestionBank,
  getInstitutionQuestionBankById,
  updateInstitutionQuestionBank,
  getAdminQuestionBankByIdInInstitution,
} from "../../helper/apicalls/questionBank";
import "../../styles/common/popup.scss";
import "../../styles/common/questionBankViewPopup.scss";

const QuestionBankViewPopup = ({
  open,
  onClose,
  questionBankId,
  context,
  contextKey,
  isAdminBank = false,
}) => {
  const [questionBank, setQuestionBank] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isGeneratorOpen, setIsGeneratorOpen] = useState(false);
  const [insertIndex, setInsertIndex] = useState(null);
  const location = useLocation();
  const isInstitution = location.pathname.startsWith("/institution");
  const isInstitutionPath =
    location.pathname.startsWith("/institution") ||
    location.pathname.startsWith("/teacher");

  useEffect(() => {
    const fetchData = async () => {
      if (!open || !questionBankId) return;
      try {
        setLoading(true);
        setError(null);
        const getByIdFn = isAdminBank
          ? getAdminQuestionBankByIdInInstitution
          : isInstitutionPath
          ? getInstitutionQuestionBankById
          : getQuestionBankById;
        const data = await getByIdFn(questionBankId);
        setQuestionBank(data || null);
      } catch (err) {
        console.error("Failed to fetch question bank by id:", err);
        setError("Unable to load question bank. Please try again.");
        setQuestionBank(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, questionBankId]);

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleAddNext = (index) => {
    setInsertIndex(index);
    setIsGeneratorOpen(true);
  };

  const handleSaveGeneratedBank = async () => {
    if (!questionBankId) {
      setIsGeneratorOpen(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const getByIdFn = isAdminBank
        ? getAdminQuestionBankByIdInInstitution
        : isInstitutionPath
        ? getInstitutionQuestionBankById
        : getQuestionBankById;
      const refreshed = await getByIdFn(questionBankId);
      setQuestionBank(refreshed || questionBank);
    } catch (err) {
      console.error("Failed to refresh question bank after generation:", err);
      setError("Failed to refresh question bank. Please try again.");
    } finally {
      setLoading(false);
      setIsGeneratorOpen(false);
    }
  };

  const handleContentBlockUpdate = async (updated) => {
    if (!questionBank || !updated || !Array.isArray(updated.content)) return;

    try {
      setLoading(true);
      setError(null);

      const payload = {
        id: questionBank.id,
        name: questionBank.name,
        duration: questionBank.duration,
        publish: questionBank.publish,
        content: updated.content,
      };

      const updateFn = isAdminBank
        ? null
        : isInstitutionPath
        ? updateInstitutionQuestionBank
        : updateQuestionBank;

      if (updateFn) {
        await updateFn(payload);
      }

      const getByIdFn = isAdminBank
        ? getAdminQuestionBankByIdInInstitution
        : isInstitutionPath
        ? getInstitutionQuestionBankById
        : getQuestionBankById;
      const refreshed = await getByIdFn(questionBank.id);
      setQuestionBank(
        refreshed || {
          ...questionBank,
          content: updated.content,
        }
      );
    } catch (err) {
      console.error("Failed to update question bank content:", err);
      setError("Failed to save changes. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (
      !questionBank ||
      !Array.isArray(questionBank.content) ||
      !questionBank.content.length
    ) {
      return <div>No content available for this question bank.</div>;
    }

    return questionBank.content.map((item, index) => {
      const contentType = item.type || "content";
      const contentToDisplay = item.content ?? item;

      switch (contentType) {
        case "mcq":
          return (
            <MCQViewer
              key={index}
              questionData={contentToDisplay}
              title={`${questionBank.name || "MCQ"} - Block ${index + 1}`}
              pageId={questionBank?.id}
              pageData={questionBank}
              context="questionbank"
              contentIndex={index}
              allowEditWithoutPersist={false}
              onUpdate={isAdminBank ? null : handleContentBlockUpdate}
              onAddNext={isAdminBank ? null : () => handleAddNext(index)}
              permission={!isAdminBank}
            />
          );
        case "trueorfalse":
          return (
            <TrueFalseViewer
              key={index}
              questionData={contentToDisplay}
              title={`${questionBank.name || "True / False"} - Block ${
                index + 1
              }`}
              pageId={questionBank?.id}
              pageData={questionBank}
              context="questionbank"
              contentIndex={index}
              allowEditWithoutPersist={false}
              onUpdate={isAdminBank ? null : handleContentBlockUpdate}
              onAddNext={isAdminBank ? null : () => handleAddNext(index)}
              permission={!isAdminBank}
            />
          );
        case "match":
          return (
            <MatchViewer
              key={index}
              questionData={contentToDisplay}
              title={`${questionBank.name || "Match"} - Block ${index + 1}`}
              pageId={questionBank?.id}
              pageData={questionBank}
              context="questionbank"
              contentIndex={index}
              allowEditWithoutPersist={false}
              onUpdate={isAdminBank ? null : handleContentBlockUpdate}
              onAddNext={isAdminBank ? null : () => handleAddNext(index)}
              permission={!isAdminBank}
            />
          );
        case "fillups":
          return (
            <FillUpsViewer
              key={index}
              questionData={contentToDisplay}
              title={`${questionBank.name || "Fill Ups"} - Block ${index + 1}`}
              pageId={questionBank?.id}
              pageData={questionBank}
              context="questionbank"
              contentIndex={index}
              allowEditWithoutPersist={false}
              onUpdate={isAdminBank ? null : handleContentBlockUpdate}
              onAddNext={isAdminBank ? null : () => handleAddNext(index)}
              permission={!isAdminBank}
            />
          );
        case "analysis":
          return (
            <AnalysisComponent
              key={index}
              data={contentToDisplay}
              title={`${questionBank.name || "Analysis"} - Block ${index + 1}`}
              pageId={questionBank?.id}
              pageData={questionBank}
              context="questionbank"
              contentIndex={index}
              allowEditWithoutPersist={false}
              onUpdate={isAdminBank ? null : handleContentBlockUpdate}
              onAddNext={isAdminBank ? null : () => handleAddNext(index)}
            />
          );
        default:
          return (
            <div key={index} className="content-preview">
              <pre>
                {typeof contentToDisplay === "string"
                  ? contentToDisplay
                  : JSON.stringify(contentToDisplay, null, 2)}
              </pre>
            </div>
          );
      }
    });
  };

  if (!open) return null;

  const effectiveContext = context || "questionbank";
  const effectiveContextKey = contextKey || questionBankId;

  const deriveInitialDataForAddNext = () => {
    if (!questionBank) return null;

    let category = "";
    if (
      Array.isArray(questionBank.content) &&
      typeof insertIndex === "number" &&
      insertIndex >= 0 &&
      insertIndex < questionBank.content.length
    ) {
      const item = questionBank.content[insertIndex] || {};
      category = item.category || item.categories || "";
    }

    return {
      name: questionBank.name || "",
      duration:
        questionBank.duration !== undefined && questionBank.duration !== null
          ? String(questionBank.duration)
          : "",
      publish: !!questionBank.publish,
      category,
    };
  };

  return (
    <>
      <div className="common-popup-overlay high-z-index" onClick={handleClose}>
        <div
          className="common-popup-container"
          onClick={(e) => e.stopPropagation()}
          style={{ minWidth: "800px" }}
        >
          <div className="popup-header with-close">
            <h2>{questionBank?.name || "Question Bank"}</h2>
            <button
              className="popup-close-button"
              onClick={handleClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="popup-content">
            <GlobalLoader visible={loading} text="Loading question bank..." />
            {error && <div className="popup-error-message">{error}</div>}
            {!loading && !error && renderContent()}
          </div>
        </div>
      </div>

      {isGeneratorOpen && (
        <GenerateQuestionBankPopup
          open={isGeneratorOpen}
          handleClose={() => setIsGeneratorOpen(false)}
          onSave={handleSaveGeneratedBank}
          initialData={deriveInitialDataForAddNext()}
          addNext={true}
          bankId={questionBankId}
          context={effectiveContext}
          contextKey={effectiveContextKey}
          existingContent={questionBank?.content || []}
          insertIndex={insertIndex}
        />
      )}
    </>
  );
};

export default QuestionBankViewPopup;
